import type { GuardianPromptV2 } from "@/services/api/guardiansv2/types";

export const SCREENING_AUDIT_TYPE = "_screening";
export const SCREENING_PERSONA = "screen";
export const SCREENING_NAV_KEY = "rules_check:ace";
export const SYNTHESIS_PERSONA = "_synthesis";

const PERSONA_ORDER = ["ace", "ivy", "nova", SYNTHESIS_PERSONA] as const;

function personaSortIndex(persona: string): number {
  const idx = PERSONA_ORDER.indexOf(persona as (typeof PERSONA_ORDER)[number]);
  return idx === -1 ? 99 : idx;
}

/** Audit types shown in the prompts nav (interaction omitted). */
export const PROMPT_AUDIT_TYPES = [
  "task_complete",
  "mission_complete",
  "artifact",
  "tool_call",
  "trigger_run",
] as const;

export type PromptAuditType = (typeof PROMPT_AUDIT_TYPES)[number];

export type PromptNavSection =
  | { kind: "rules_check"; label: "Rules check" }
  | { kind: "separator" }
  | { kind: "audit"; auditType: PromptAuditType; label: string };

export const PROMPT_NAV_SECTIONS: PromptNavSection[] = [
  { kind: "rules_check", label: "Rules check" },
  { kind: "separator" },
  { kind: "audit", auditType: "task_complete", label: "Task" },
  { kind: "audit", auditType: "mission_complete", label: "Mission" },
  { kind: "audit", auditType: "artifact", label: "Artefact" },
  { kind: "audit", auditType: "tool_call", label: "Tool call" },
  { kind: "audit", auditType: "trigger_run", label: "Trigger" },
];

const AUDIT_TYPE_LABELS: Record<PromptAuditType, string> = {
  task_complete: "Task",
  mission_complete: "Mission",
  artifact: "Artefact",
  tool_call: "Tool call",
  trigger_run: "Trigger",
};

export function promptNavKey(auditType: string, persona: string): string {
  if (auditType === SCREENING_AUDIT_TYPE) return SCREENING_NAV_KEY;
  return `${auditType}:${persona}`;
}

export function parsePromptNavKey(key: string): { auditType: string; persona: string } | null {
  if (key === SCREENING_NAV_KEY) {
    return { auditType: SCREENING_AUDIT_TYPE, persona: SCREENING_PERSONA };
  }
  const sep = key.indexOf(":");
  if (sep <= 0) return null;
  return { auditType: key.slice(0, sep), persona: key.slice(sep + 1) };
}

export function auditTypeHeading(auditType: string): string {
  if (auditType === SCREENING_AUDIT_TYPE) return "Rules check";
  const match = PROMPT_NAV_SECTIONS.find(
    (s): s is Extract<PromptNavSection, { kind: "audit" }> =>
      s.kind === "audit" && s.auditType === auditType
  );
  if (match) return match.label;
  return auditType.replace(/_/g, " ");
}

export function personaLabel(persona: string): string {
  if (persona === SYNTHESIS_PERSONA) return "Synthesis";
  if (persona === SCREENING_PERSONA) return "Ace";
  if (persona === "ace") return "Ace";
  if (persona === "ivy") return "Ivy";
  if (persona === "nova") return "Nova";
  return persona;
}

export function groupAuditPrompts(prompts: GuardianPromptV2[]): Map<string, GuardianPromptV2[]> {
  const groups = new Map<string, GuardianPromptV2[]>();
  for (const auditType of PROMPT_AUDIT_TYPES) {
    const items = prompts.filter((p) => p.audit_type === auditType);
    if (items.length === 0) continue;
    items.sort((a, b) => personaSortIndex(a.persona) - personaSortIndex(b.persona));
    groups.set(auditType, items);
  }
  return groups;
}

export function findScreeningPrompt(prompts: GuardianPromptV2[]): GuardianPromptV2 | null {
  return (
    prompts.find((p) => p.audit_type === SCREENING_AUDIT_TYPE && p.persona === SCREENING_PERSONA) ??
    null
  );
}

export function defaultPromptNavKey(prompts: GuardianPromptV2[]): string | null {
  if (findScreeningPrompt(prompts)) return SCREENING_NAV_KEY;
  const grouped = groupAuditPrompts(prompts);
  const firstType = PROMPT_AUDIT_TYPES.find((t) => grouped.has(t));
  if (!firstType) return null;
  const first = grouped.get(firstType)?.[0];
  return first ? promptNavKey(first.audit_type, first.persona) : null;
}

export { AUDIT_TYPE_LABELS };
